/** Gate a messaging action behind its channel being enabled.
 *
 * Messaging actions flag their channel in `detail.channel`. If it isn't on yet, this
 * shows an "Enable X channel" button (manage-gated) instead of the provider-setup UI;
 * once enabled, the children (the setup control) render. Channels used to be fixed at
 * TO creation — this lets ops turn one on, then do the setup. */
import { Alert, Box, Button } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

import type { ActionUIProps } from './ActionCustomUI';

const CHANNEL_LABELS: Record<string, string> = { sms: 'SMS', whatsapp: 'WhatsApp', push: 'push' };

export function ChannelEnableGate({ item, topId, canManage, onChanged, children }: ActionUIProps & { children: ReactNode }) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const channel = typeof item.detail?.channel === 'string' ? item.detail.channel : '';
  const enabled = Boolean(item.detail?.channel_enabled);

  const mutation = useMutation({
    mutationFn: () => items.enableChannel(topId, item.id),
    onSuccess: () => {
      snackbar.show('Channel enabled.', 'success');
      onChanged();
      void qc.invalidateQueries({ queryKey: ['preflight'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not enable the channel.', 'error'),
  });

  // No channel flag (non-messaging) or already on → show the setup UI as-is.
  if (!channel || enabled) return <>{children}</>;

  const label = CHANNEL_LABELS[channel] ?? channel;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Alert
        severity="warning"
        action={
          canManage ? (
            <Button color="inherit" size="small" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? 'Enabling…' : `Enable ${label}`}
            </Button>
          ) : undefined
        }
      >
        The {label} channel isn’t enabled for this operator yet. Enable it to set up {label}.
      </Alert>
    </Box>
  );
}
